import React from 'react'
// import { SearchFields } from "@pulseops/outbound"
import { ControllerFieldState, ControllerRenderProps, UseFormReturn, UseFormStateReturn } from "react-hook-form"
import { assets, DatePicker, Input, Select, SelectOption, SelectSearch, ServiceInquiryCategories, SourceList, TransactionType, TransactionTypeSelectOptions } from '@pulseops/common'
import { BasicSearchForm } from '../service-inquiry/basic-search-form'
import { TFunction } from 'react-i18next'
import { BasicSearchConst } from './BasicSearchConst'

export type BasicFieldTypeNames = 'policyNumber' | 'idNumber' | 'clientNumber' | 'fromDate' | 'toDate' | 'source' | 'category' | 'transactionType'

interface SearchedInputField {
  formName: string,
  type: 'INPUT',
  title: string,
  inputType: string
  maxLength?: number
  rules?: Object
  placeholder?: string
}

interface SelectedField {
  formName: string,
  type: 'SELECT',
  title: string,
  option: SelectOption[],
  rules?: Object
}

interface SearchedSelectField {
  formName: string,
  type: 'SEARCHSELECT',
  title: string,
  option: SelectOption[],
  onChange?: (value: SelectOption | null) => void
  rules?: Object
}

interface SearchedDateField {
  formName: string,
  type: 'DATE',
  required?: boolean,
  title: string
  maxDate?: Date,
  maxDateMessage?: string
  minDate?: Date,
  minDateMessage?: string
  rules?: Object
}

interface MutilSearchedSelectField {
  formName: string,
  type: 'MUTILSEARCHSELECT',
  title: string,
  option: SelectOption[],
  onChange?: (value: SelectOption[] | null) => void
  rules?: Object
}


export type FieldSearch = SearchedInputField | SelectedField | SearchedSelectField | SearchedDateField | MutilSearchedSelectField
export type InputType = 'text' | 'number' | 'money'

export const basicSearchFieldList = (
  t: TFunction<"translation">,
  base: Omit<UseFormReturn<BasicSearchForm.Raw>, 'handleSubmit'>,
  category: SelectOption | null,
  subServiceTypeList: SelectOption[],
  subTransactionTypeList: Array<SelectOption & { typeCode: string }>
): FieldSearch[] => [
    {
      formName: 'policyNumber',
      type: 'INPUT',
      title: t('ServiceInquiry:PolicyNumber'),
      inputType: 'number',
      maxLength: 8,
      placeholder: t('ServiceInquiry:EnterPolicy'),
    },
    {
      formName: 'clientNumber',
      type: 'INPUT',
      title: t('ServiceInquiry:ClientNumber'),
      inputType: 'number',
      maxLength: 8,
      placeholder: t('ServiceInquiry:EnterClientNumber'),
    },
    {
      formName: 'idNumber',
      type: 'INPUT',
      title: t('ServiceInquiry:IDNumber'),
      inputType: 'text',
      placeholder: t('ServiceInquiry:EnterIDNumber'),
    },
    {
      formName: 'fromDate',
      type: 'DATE',
      title: t('ServiceInquiry:FromDate'),
      required: true,
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },
    {
      formName: 'toDate',
      type: 'DATE',
      title: t('ServiceInquiry:ToDate'),
      required: true,
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },
    {
      formName: 'source',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:Source'),
      option: SourceList.concat(BasicSearchConst.selectOptionOBAndIB)
    },
    {
      formName: 'category',
      type: 'SEARCHSELECT',
      title: t('ServiceInquiry:Category'),
      option: ServiceInquiryCategories.map((item) => ({
        label: t(`Reports:${item.label}`),
        value: item.value
      })),
      onChange: (_) => {
        base.setValue('transactionType', [])
        base.setValue('subServiceType', [])
        base.setValue('subTransactionType', [])
      }
    },
    {
      formName: 'transactionType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:TransactionType'),
      option: TransactionTypeSelectOptions.filter(
        (x) => x.category === category?.value
      ).map((item) => ({
        label: t(item.label),
        value: item.value
      }))
    },
    {
      formName: 'subServiceType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:SubServiceType'),
      option: subServiceTypeList,
      onChange: (_) => {
        base.setValue('subTransactionType', [])
      }
    },
    {
      formName: 'subTransactionType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:SubTransactionType'),
      option: subTransactionTypeList.filter((x) =>
        base
          .watch('subServiceType')
          .map((_) => _.value)
          .includes(x.typeCode)
      )
    },
  ]

export const advancedSearchFieldList = (
  t: TFunction<"translation">,
  base: Omit<UseFormReturn<BasicSearchForm.Raw>, 'handleSubmit'>,
  officeOptions: SelectOption[],
  subServiceTypeList: SelectOption[],
  subTransactionTypeList: Array<SelectOption & { typeCode: string }>
): FieldSearch[] => [
    {
      formName: 'fromDate',
      type: 'DATE',
      title: t('ServiceInquiry:FromDate'),
      required: true,
      // rules: {
      //   required: { value: true, message: t('message:MS020009', { field: t('ServiceInquiry:FromDate') }) }
      // },
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },
    {
      formName: 'toDate',
      type: 'DATE',
      title: t('ServiceInquiry:ToDate'),
      required: true,
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },
    {
      formName: 'source',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:Source'),
      option: SourceList.concat(BasicSearchConst.selectOptionOBAndIB)
    },
    {
      formName: 'offices',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:Office'),
      option: officeOptions
    },
    {
      formName: 'category',
      type: 'SEARCHSELECT',
      title: t('ServiceInquiry:Category'),
      option: ServiceInquiryCategories.map((item) => ({
        label: t(`Reports:${item.label}`),
        value: item.value
      })),
      onChange: (_) => {
        base.setValue('transactionType', [])
        base.setValue('subServiceType', [])
        base.setValue('subTransactionType', [])
      }
    },
    {
      formName: 'transactionType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:TransactionType'),
      option: TransactionTypeSelectOptions.filter(
        (x) => x.category === base.watch('category')?.value
      ).map((item) => ({
        label: t(item.label),
        value: item.value
      }))
    },
    {
      formName: 'subServiceType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:SubServiceType'),
      option: subServiceTypeList,
      onChange: (_) => {
        base.setValue('subTransactionType', [])
      }
    },
    {
      formName: 'subTransactionType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:SubTransactionType'),
      option: subTransactionTypeList.filter((x) =>
        base
          .watch('subServiceType')
          .map((_) => _.value)
          .includes(x.typeCode)
      )
    },
  ]

export const adminClaimSearchFieldList = (
  t: TFunction<"translation">,
  claimTypeOptions: SelectOption[]
): FieldSearch[] => [
    {
      formName: 'policyNumberClaim',
      type: 'INPUT',
      title: t('ServiceInquiry:PolicyNumber'),
      inputType: 'text',
      maxLength: 9,
      placeholder: t('ServiceInquiry:EnterPolicy'),
    },
    {
      formName: 'claimNumber',
      type: 'INPUT',
      title: t('ServiceInquiry:ClaimNumber'),
      inputType: 'text',
      maxLength: 11,
      placeholder: t('ServiceInquiry:EnterClaimNumber'),
    },
    {
      formName: 'idNumber',
      type: 'INPUT',
      title: t('ServiceInquiry:IDNumber'),
      inputType: '24',
      maxLength: 11,
      placeholder: t('ServiceInquiry:EnterIDNumber'),
    },
    {
      formName: 'fromDate',
      type: 'DATE',
      title: t('ServiceInquiry:FromDate'),
      required: true,
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },
    {
      formName: 'toDate',
      type: 'DATE',
      title: t('ServiceInquiry:ToDate'),
      required: true,
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },

    {
      formName: 'transactionType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:TransactionType'),
      option: [
        {
          label: t(`TransactionType:${TransactionType.NEW_CLAIM_REGISTER}`),
          value: TransactionType.NEW_CLAIM_REGISTER
        },
        {
          label: t(`TransactionType:${TransactionType.PAYOUT}`),
          value: TransactionType.PAYOUT
        }
      ]
    },
    {
      formName: 'source',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:Source'),
      option: SourceList.concat(BasicSearchConst.selectOptionOBAndIB)
    },
    {
      formName: 'claimTypes',
      type: 'SEARCHSELECT',
      title: t('ServiceInquiry:ClaimType'),
      option: claimTypeOptions
    },

  ]

export const claimSearchFieldList = (
  t: TFunction<"translation">,
  claimTypeOptions: SelectOption[]
): FieldSearch[] => [
    {
      formName: 'policyNumberClaim',
      type: 'INPUT',
      title: t('ServiceInquiry:PolicyNumber'),
      inputType: 'number',
      maxLength: 9,
      placeholder: t('ServiceInquiry:EnterPolicy'),
    },
    {
      formName: 'claimNumber',
      type: 'INPUT',
      title: t('ServiceInquiry:ClaimNumber'),
      inputType: 'text',
      maxLength: 11,
      placeholder: t('ServiceInquiry:EnterClaimNumber'),
    },
    {
      formName: 'idNumber',
      type: 'INPUT',
      title: t('ServiceInquiry:IDNumber'),
      inputType: '24',
      maxLength: 11,
      placeholder: t('ServiceInquiry:EnterIDNumber'),
    },
    {
      formName: 'fromDate',
      type: 'DATE',
      title: t('ServiceInquiry:FromDate'),
      required: true,
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },
    {
      formName: 'toDate',
      type: 'DATE',
      title: t('ServiceInquiry:ToDate'),
      required: true,
      maxDate: new Date(),
      maxDateMessage: t('message:MS990032')
    },
    {
      formName: 'claimTypes',
      type: 'SEARCHSELECT',
      title: t('ServiceInquiry:ClaimType'),
      option: claimTypeOptions,
    },
    {
      formName: 'source',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:Source'),
      option: SourceList.concat(BasicSearchConst.selectOptionOBAndIB)
    },
    {
      formName: 'transactionType',
      type: 'MUTILSEARCHSELECT',
      title: t('ServiceInquiry:TransactionType'),
      option: [
        {
          label: t(`TransactionType:${TransactionType.NEW_CLAIM_REGISTER}`),
          value: TransactionType.NEW_CLAIM_REGISTER
        },
        {
          label: t(`TransactionType:${TransactionType.PAYOUT}`),
          value: TransactionType.PAYOUT
        }
      ]
    }
  ]

export const getFieldSearch = ({
  field,
  controller,
}: {
  field: FieldSearch
  controller: {
    field: ControllerRenderProps<BasicSearchForm.Raw, BasicFieldTypeNames>
    fieldState: ControllerFieldState,
    formState: UseFormStateReturn<BasicSearchForm.Raw>
  },
}) => {
  switch (field.type) {
    case 'INPUT':
      return (
        <Input
          title={field.title}
          value={controller.field.value as string | undefined}
          maxLength={field.maxLength}
          inputType={field.inputType as InputType}
          onChange={controller.field.onChange}
          errorMessage={controller.fieldState.error?.message}
        />
      )
    case 'DATE':
      return (
        <DatePicker
          required={field.required}
          label={field.title}
          onChange={controller.field.onChange}
          value={controller.field.value as Date | undefined | null}
          maxDate={field.maxDate}
          maxDateMessage={field.maxDateMessage}
          minDate={field.minDate}
          minDateMessage={field.minDateMessage}
          errorMessage={controller.fieldState.error?.message}
        />
      )
    case 'SELECT':
      return (
        <Select
          label={field.title}
          options={field.option}
          onChange={controller.field.onChange}
          value={controller.field.value as SelectOption | undefined | null}
          errorMessage={controller.fieldState.error?.message}
        />
      )
    case 'SEARCHSELECT':
      return (
        <SelectSearch
          // multiple={true}
          label={field.title}
          options={field.option}
          popupIcon={<assets.ArrowDownDropdownIcon />}
          onChange={(val) => {
            field.onChange && field.onChange(val)
            controller.field.onChange(val)
          }}
          value={controller.field.value as SelectOption | undefined | null}
          errorMessage={controller.fieldState.error?.message}
        />
      )
    case 'MUTILSEARCHSELECT':
      return (
        <SelectSearch
          multiple={true}
          label={field.title}
          options={field.option}
          popupIcon={<assets.ArrowDownDropdownIcon />}
          onChange={(val) => {
            field.onChange && field.onChange(val)
            controller.field.onChange(val)
          }}
          value={controller.field.value as SelectOption[] | undefined}
          errorMessage={controller.fieldState.error?.message}
        />
      )
    default:
      return <></>
  }
}